/**
 * <RapidInstallation /> — Step 4 of the Absorption Flow (ZOX-style).
 *
 * CRITICAL CONCEPT: this stage bypasses the conscious mind. The user's
 * conscious attention is occupied by a small glowing fixation dot at the
 * top of the screen. Information is flashed at 100–300ms per card in a
 * rectangle below — too fast to consciously read.
 *
 * Strict rules (per user spec):
 *   - NO buttons
 *   - NO progress bar
 *   - NO pause / skip
 *   - NO interaction at all
 *
 * Layout:
 *   TOP          — small fixation dot, soft pulse
 *   INSTRUCTION  — "Keep your eyes gently on the light…" (fades after ~6s)
 *   FLASH ZONE   — rectangular area below the dot where cards flash
 *
 * Behaviour:
 *   - Cadence: 130–280ms per flash (under conscious-read speed)
 *   - 30–50% of flashes are rotated 180° (prevents conscious reading,
 *     reinforces subconscious processing)
 *   - Some flashes carry an image (driving scenarios)
 *   - Smooth motion-blur dissolve between flashes (no strobe)
 *   - State guidance text whispers in periodically
 *
 * Exit:
 *   - At end, smoothly fades the whole screen to navy, then auto-flows
 *     directly into <ImmersionPlayer> (no pause screen, no UI break)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  JOURNEY_PALETTE as P,
  JOURNEY_FONT_HEAD,
  JOURNEY_FONT_BODY,
  JOURNEY_FONT_LABEL,
} from "./journeyTheme";
import { useAttentionGuard } from "./useAttentionGuard";
import {
  getAttentionGuardEnabled,
  isAttentionGuardDecided,
} from "./AttentionGuardConsent";
import { loadFamiliarity, scoreFamiliarity } from "./familiarityStore";

const API = process.env.REACT_APP_BACKEND_URL;

// Build the flash queue from the day's content.
// Each entry is a coherent card-shaped unit: { front, back, image, kind }
// — the user's subconscious sees the EXACT same Situation+Answer+Image
// triad they just consciously studied, just at flash speed.
function buildQueue(content) {
  const items = [];
  const allCards = [
    ...(content?.cards?.flash || []).map((c) => ({ ...c, kind: "flash" })),
    ...(content?.cards?.precision || []).map((c) => ({ ...c, kind: "precision" })),
    ...(content?.cards?.scenario || []).map((c) => ({ ...c, kind: "scenario" })),
  ];
  for (const c of allCards) {
    const img = c.image_url
      ? (c.image_url.startsWith("http") ? c.image_url : `${API}${c.image_url}`)
      : null;
    if (!c.front && !c.back) continue;
    // Trim back to a tight ~14-word lead clause for fast subconscious read.
    let backLead = "";
    if (c.back) {
      const lead = String(c.back).split(/[—.,;:]/)[0]?.trim() || String(c.back);
      backLead = lead.split(/\s+/).slice(0, 14).join(" ");
    }
    // Push the COMBINED card (front + back), the way it appeared in
    // CardDeck. Always keep the image with the combined version so
    // the user's subconscious recognises the same visual triad.
    items.push({
      front: c.front ? String(c.front) : "",
      back: backLead,
      image: img,
      kind: c.kind,
    });
    // Then push the front + back as SEPARATE flashes (no image). This
    // is what fixes the "Bible · Day 1 · firmament" loop bug: a day
    // with only 2-3 cards used to flash the same combined card over
    // and over for 70 seconds. Splitting fronts/backs into their own
    // flashes gives the queue 3x more unique entries without inventing
    // new content, and the subconscious reinforcement is even cleaner
    // (single-concept flashes hit faster).
    if (c.front && c.back) {
      items.push({ front: String(c.front), back: "", image: null, kind: c.kind });
      items.push({ front: "", back: backLead, image: null, kind: c.kind });
    }
  }
  // Pull in quiz prompts too — concept-only flashes that the user has
  // already engaged with consciously. These usually share vocabulary
  // with the cards which deepens the imprint.
  for (const q of content?.quiz?.questions || []) {
    if (q.prompt) items.push({ front: String(q.prompt), back: "", image: null, kind: "quiz" });
  }
  if (content?.lesson?.title) {
    items.push({ front: content.lesson.title, back: "", image: null, kind: "lesson" });
  }
  // Lesson summary, broken into ~10-word slivers — adds a final layer of
  // contextual variety so short-content days never loop the same flash.
  const lessonSummary = content?.lesson?.summary || content?.lesson?.body;
  if (lessonSummary) {
    String(lessonSummary)
      .split(/[—.;:]/)
      .map((s) => s.trim())
      .filter((s) => s && s.length > 2)
      .forEach((s) => {
        const sliver = s.split(/\s+/).slice(0, 10).join(" ");
        items.push({ front: sliver, back: "", image: null, kind: "summary" });
      });
  }
  for (const w of content?.immersion?.words || []) {
    if (w.text) items.push({ front: w.text, back: "", image: null, kind: "word" });
  }
  // Hypnotic shuffle
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}

// State guidance — appears mid-sequence, then fades.
const STATE_GUIDANCE = [
  { atSec: 4,  text: "Stay relaxed…" },
  { atSec: 16, text: "Let it pass through you…" },
  { atSec: 32, text: "You don't need to follow it…" },
  { atSec: 50, text: "Just keep your eyes on the light…" },
];

// Tunables — adaptive cadence per familiarity bucket
const FLASH_MS_FAMILIAR    = 180; // already lit up — faster reinforcement
const FLASH_MS_UNFAMILIAR  = 280; // weak trace — longer imprint window
const FLASH_MS_UNKNOWN_MIN = 150; // no quiz signal → fall back to wide range
const FLASH_MS_UNKNOWN_MAX = 250;
const ROTATE_RATIO  = 0.40;  // 40% of flashes are rotated 180°
const TOTAL_TARGET_S = 70;   // total stage duration target (~60–90s)
const CUE_FADE_MS   = 5500;  // when the "Keep eyes on the light" cue fades

export default function RapidInstallation({ content, slug, day, onContinue, flashOnly = false }) {
  // Adaptive familiarity map from the user's quiz answers (if taken).
  const familiarity = useMemo(() => loadFamiliarity(slug, day), [slug, day]);
  const queue = useMemo(() => buildQueue(content), [content]);
  // Repeat the queue if needed so the rapid stage can run for ~70s.
  // Each flash averages ~205ms, so we need ~340 events for 70s. We
  // RE-SHUFFLE every full cycle so a short queue (e.g. day 1 of a
  // brand-new personal journey with only 2 cards) never shows the
  // same flash twice in a row — fixes the "what is the firmament"
  // loop bug reported on the Bible · Day 1 · Genesis journey.
  const items = useMemo(() => {
    if (queue.length === 0) return [];
    const target = Math.ceil((TOTAL_TARGET_S * 1000) / 205);
    const out = [];
    let cycle = queue.slice();
    let lastFront = "";
    while (out.length < target) {
      if (cycle.length === 0) {
        // re-fill + Fisher-Yates shuffle for the next cycle
        cycle = queue.slice();
        for (let i = cycle.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [cycle[i], cycle[j]] = [cycle[j], cycle[i]];
        }
        // Avoid the new cycle's first item being identical to the
        // previous cycle's last item — common when the queue has
        // very few unique entries.
        if (cycle.length > 1 && cycle[0]?.front === lastFront) {
          const swapIdx = 1 + Math.floor(Math.random() * (cycle.length - 1));
          [cycle[0], cycle[swapIdx]] = [cycle[swapIdx], cycle[0]];
        }
      }
      const next = cycle.shift();
      out.push(next);
      lastFront = next?.front || lastFront;
    }
    // Per-item rotation + per-item familiarity score → personal dwell time.
    return out.map((it) => {
      const probeText = `${it.front || ""} ${it.back || ""}`.trim();
      const score = familiarity ? scoreFamiliarity(probeText, familiarity) : "unknown";
      let dwell;
      if (score === "familiar") dwell = FLASH_MS_FAMILIAR;
      else if (score === "unfamiliar") dwell = FLASH_MS_UNFAMILIAR;
      else dwell = FLASH_MS_UNKNOWN_MIN + Math.random() * (FLASH_MS_UNKNOWN_MAX - FLASH_MS_UNKNOWN_MIN);
      // Rotation variety — the user wants MORE upside-down moments
      // plus visual variation. ~55% of flashes carry a non-zero
      // rotation: 70% are upside-down (180°), 15% are ±90° quarter
      // turns, 15% are gentle ±15° tilts. The composition stays
      // anchored on the centre dot so the eye doesn't chase.
      const r = Math.random();
      let rotateDeg = 0;
      if (r < 0.55) {
        const variant = Math.random();
        if (variant < 0.70) rotateDeg = 180;
        else if (variant < 0.85) rotateDeg = Math.random() < 0.5 ? 90 : -90;
        else rotateDeg = (Math.random() < 0.5 ? 12 : -12);
      }
      return {
        ...it,
        rotated: rotateDeg !== 0,           // legacy flag kept for any callers
        rotateDeg,
        familiarity: score,
        dwellMs: Math.round(dwell),
      };
    });
  }, [queue, familiarity]);

  const [index, setIndex] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const startedRef = useRef(Date.now());
  const completedRef = useRef(false);
  // ─── Voice narration audio ────────────────────────────────────────
  // Michael's "Rapid Installation" guidance: ~65s, soft, ends on
  // "And now, let's go deeper." We start it on mount, keep it muted
  // if the user has previously toggled audio off, and let it play
  // through naturally. The flash schedule is independent — the
  // 70-second TOTAL_TARGET_S already lines up with the voice arc.
  const audioRef = useRef(null);
  const musicRef = useRef(null);
  const RAPID_VOICE_URL = `${API}/api/audio/voice/absorption/rapid_installation.m4a`;
  // Subtle ambient drone underneath the voice — same C-minor "Dissolve"
  // bed as the Reclaim ritual's first half. ~−20 dB so it sits well
  // beneath Michael's narration and never competes with the flash.
  const RAPID_MUSIC_URL = `${API}/api/audio/voice/rewrite_engine/clearing/music_dissolve.mp3`;
  const RAPID_MUSIC_TARGET_VOL = 0.10;     // gentle bed (-20 dB)
  const RAPID_MUSIC_FADE_MS = 1600;        // soft entry / exit
  useEffect(() => {
    const a = new Audio(RAPID_VOICE_URL);
    a.preload = "auto";
    a.volume = 0.9;
    audioRef.current = a;
    // Try to start. Browsers require a prior user gesture to play
    // audio — the user has already tapped through the absorption
    // flow's prior stages, so this should succeed. If it doesn't,
    // we silently fall back to a muted state and the visual flash
    // continues to work.
    const tryPlay = () => {
      const p = a.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => { /* autoplay blocked — silent fallback */ });
      }
    };
    tryPlay();
    // Music bed — fades in over ~1.6s so the voice arrives a beat
    // before the music settles. On unmount we fade out + pause.
    const m = new Audio(RAPID_MUSIC_URL);
    m.preload = "auto";
    m.loop = false;
    m.volume = 0;
    musicRef.current = m;
    const mp = m.play();
    if (mp && typeof mp.catch === "function") {
      mp.catch(() => { /* autoplay blocked */ });
    }
    // Volume ramp helper (16-step linear). Returns the timer id.
    const ramp = (el, from, to, ms) => {
      if (!el) return null;
      const steps = 16;
      const dt = ms / steps;
      let i = 0;
      el.volume = from;
      const tid = setInterval(() => {
        i += 1;
        const v = from + (to - from) * (i / steps);
        try { el.volume = Math.max(0, Math.min(1, v)); } catch (_e) { /* */ }
        if (i >= steps) clearInterval(tid);
      }, dt);
      return tid;
    };
    const fadeInTid = ramp(m, 0, RAPID_MUSIC_TARGET_VOL, RAPID_MUSIC_FADE_MS);

    // Pause + clean up on unmount or hard nav away.
    const onHide = () => {
      try { a.pause(); } catch (_e) { /* */ }
      try { m.pause(); } catch (_e) { /* */ }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      if (fadeInTid) clearInterval(fadeInTid);
      // Soft fade-out for the music bed; voice cuts immediately because
      // it's already either finished or being interrupted by the next
      // stage's cinematic fade.
      try {
        const startVol = m.volume;
        ramp(m, startVol, 0, 900);
        setTimeout(() => { try { m.pause(); m.src = ""; } catch (_e) { /* */ } }, 950);
      } catch (_e) { /* */ }
      try { a.pause(); a.src = ""; } catch (_e) { /* */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Stable refs — prevents the flash interval from being torn down every
  // render when parent passes a fresh `onContinue` arrow function.
  const onContinueRef = useRef(onContinue);
  useEffect(() => { onContinueRef.current = onContinue; }, [onContinue]);
  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);
  const total = items.length;

  // Attention guard — only runs if the user opted in via consent prompt.
  const attentionEnabled = isAttentionGuardDecided() && getAttentionGuardEnabled();
  const { drift, drifts } = useAttentionGuard({ enabled: attentionEnabled });

  // Tick once a second for guidance + total elapsed
  useEffect(() => {
    const id = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedRef.current) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Flash scheduler — adaptive dwell baked per item by the items memo.
  // Uses refs for `onContinue` and `items` so a parent re-render that
  // creates a fresh callback ref doesn't tear down the in-flight setTimeout
  // (this was the v4 "rapid doesn't work at all" bug).
  useEffect(() => {
    if (total === 0) {
      onContinueRef.current?.();
      return;
    }
    if (index >= total) {
      if (!completedRef.current) {
        completedRef.current = true;
        setExiting(true);
        const t = setTimeout(() => onContinueRef.current?.(), 900);
        return () => clearTimeout(t);
      }
      return;
    }
    const dwell = itemsRef.current[index]?.dwellMs ?? 205;
    const t = setTimeout(() => setIndex((i) => i + 1), dwell);
    return () => clearTimeout(t);
  }, [index, total]);

  // Active state-guidance line (cumulative — last one whose atSec <= now)
  const guidance = STATE_GUIDANCE.reduce(
    (cur, g) => (elapsedSec >= g.atSec ? g : cur),
    null
  );
  const showCue = elapsedSec * 1000 < CUE_FADE_MS;

  const current = items[Math.min(index, total - 1)];

  return (
    <motion.div
      className="relative min-h-screen overflow-hidden"
      style={{ background: P.bgWash }}
      initial={{ opacity: 0 }}
      animate={{ opacity: exiting ? 0 : 1 }}
      transition={{ duration: exiting ? 0.9 : 0.6, ease: "easeInOut" }}
      data-testid="rapid-installation"
    >
      {/* Faint horizon glow — keeps the eye relaxed centre */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 30% at 50% 28%, rgba(77,163,255,0.12) 0%, rgba(77,163,255,0) 70%)",
        }}
        aria-hidden
      />

      {/* ─── Fixation dot — the user's conscious attention sits here ──
           When the attention guard detects the user looking away >3s, the
           dot pulses one extra strong cycle to call them back.
           Bigger 22 px dot per user feedback — feels grounded, not specky.
           A tiny "R" sits inside the orb — Reclaim sigil at the centre
           of every immersion. Experimental: rolling out here first
           before universalising across the whole site. */}
      {!flashOnly && (
      <motion.div
        className="absolute flex items-center justify-center"
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          top: "16%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background:
            "radial-gradient(circle at 50% 40%, rgba(255,255,255,1) 0%, rgba(196,222,255,0.92) 55%, rgba(196,222,255,0.0) 100%)",
          boxShadow:
            "0 0 32px rgba(255,255,255,0.65), 0 0 80px rgba(77,163,255,0.55)",
        }}
        animate={
          drift
            ? { scale: [1, 1.85, 1], opacity: [0.9, 1, 0.9] }
            : { scale: [1, 1.20, 1], opacity: [0.9, 1, 0.9] }
        }
        transition={
          drift
            ? { duration: 1.4, ease: "easeInOut" }
            : { duration: 4, repeat: Infinity, ease: "easeInOut" }
        }
        data-testid="rapid-fixation-dot"
        aria-hidden
      >
        <span
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1,
            color: "rgba(20, 40, 80, 0.85)",
            letterSpacing: "0.02em",
            // Position slightly above the geometric centre so it sits
            // visually centred inside the radial-gradient orb (which
            // has its brightest point at 40% from the top).
            transform: "translateY(-0.5px)",
            // Tiny subtle text shadow makes the R legible against
            // the bright orb without looking stamped or hard.
            textShadow: "0 0 2px rgba(255,255,255,0.4)",
            pointerEvents: "none",
            userSelect: "none",
          }}
          data-testid="rapid-fixation-dot-sigil"
        >
          R
        </span>
      </motion.div>
      )}
      {/* Attention-guard alert halo — fades in only when drift is signalled */}
      <AnimatePresence>
        {drift && (
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.85, scale: 1.6 }}
            exit={{ opacity: 0, scale: 1.9 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="absolute pointer-events-none"
            style={{
              width: 76,
              height: 76,
              borderRadius: 999,
              top: "16%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              border: "1px solid rgba(255,255,255,0.55)",
              boxShadow: "0 0 28px rgba(196,222,255,0.55)",
            }}
            data-testid="rapid-fixation-alert"
            aria-hidden
          />
        )}
      </AnimatePresence>
      {/* Hidden drift counter for tests */}
      <span
        className="sr-only"
        data-testid="attention-drift-count"
      >{drifts}</span>

      {/* Initial cue — "Keep your eyes gently on the light…" */}
      <AnimatePresence>
        {!flashOnly && showCue && !exiting && (
          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 0.78, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
            className="absolute italic text-center px-8 max-w-md"
            style={{
              top: "23%",
              left: 0,
              right: 0,
              color: "rgba(255,255,255,0.85)",
              fontFamily: JOURNEY_FONT_HEAD,
              fontWeight: 300,
              fontSize: "clamp(13px, 2.4vw, 17px)",
              letterSpacing: "0.01em",
            }}
            data-testid="rapid-cue"
          >
            Keep your eyes gently on the light…
          </motion.p>
        )}
      </AnimatePresence>

      {/* ─── FLASH ZONE — same visual format as CardDeck ────────────
          Image on top half, text on bottom — so the user's subconscious
          recognises the EXACT same card shape they just consciously
          studied. Rotated 180° on ~40% of flashes (per spec) to prevent
          conscious reading. */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: flashOnly ? "8%" : "42%",
          left: "50%",
          transform: "translateX(-50%)",
          width: flashOnly ? "min(82vw, 340px)" : "min(86vw, 380px)",
          height: flashOnly ? "min(72%, 260px)" : "min(48vh, 410px)",
          borderRadius: 22,
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          boxShadow:
            "0 18px 50px rgba(0,0,0,0.35), 0 0 28px rgba(77,163,255,0.10)",
          overflow: "hidden",
        }}
        data-testid="rapid-flash-zone"
      >
        <AnimatePresence>
          {current && !exiting && (
            <div
              key={`wrap-${index}`}
              className="absolute inset-0"
              style={{
                transform: current.rotateDeg
                  ? `rotate(${current.rotateDeg}deg)`
                  : current.rotated ? "rotate(180deg)" : "none",
              }}
            >
              <motion.div
                key={`${index}`}
                initial={{ opacity: 0, scale: 0.97, filter: "blur(8px)" }}
                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                exit={{ opacity: 0, scale: 1.02, filter: "blur(6px)" }}
                transition={{
                  opacity: { duration: 0.10, ease: "easeOut" },
                  scale:   { duration: 0.14, ease: [0.16, 1, 0.3, 1] },
                  filter:  { duration: 0.10, ease: "easeOut" },
                }}
                className="absolute inset-0"
              >
              {/* Blurred image fills the whole card behind the text — keeps
                  the visual recognition pattern firing at subconscious speed
                  without distracting the eye from the centre fixation dot. */}
              {current.image && (
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${current.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "blur(14px) saturate(0.85) brightness(0.55)",
                    transform: "scale(1.15)",
                  }}
                  aria-hidden
                />
              )}
              {/* Navy wash so text always reads */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(13,27,42,0.55) 0%, rgba(13,27,42,0.82) 100%)",
                }}
                aria-hidden
              />

              {/* Stacked text: Situation (bold) above, Answer (lighter) below */}
              <div className="relative h-full flex flex-col items-center justify-center px-5 gap-3">
                {current.front && (
                  <p
                    className="text-center"
                    style={{
                      color: "#FFFFFF",
                      fontFamily: JOURNEY_FONT_HEAD,
                      fontWeight: 600,
                      fontSize: "clamp(17px, 3.3vw, 22px)",
                      letterSpacing: "-0.005em",
                      lineHeight: 1.28,
                      textShadow: "0 4px 22px rgba(13,27,42,0.65)",
                      maxWidth: "22ch",
                    }}
                    data-testid="rapid-front"
                  >
                    {current.front}
                  </p>
                )}
                {current.back && (
                  <p
                    className="text-center"
                    style={{
                      color: "rgba(255,255,255,0.72)",
                      fontFamily: JOURNEY_FONT_BODY,
                      fontWeight: 300,
                      fontSize: "clamp(13px, 2.4vw, 16px)",
                      letterSpacing: "0.005em",
                      lineHeight: 1.42,
                      textShadow: "0 2px 14px rgba(13,27,42,0.55)",
                      maxWidth: "26ch",
                    }}
                    data-testid="rapid-back"
                  >
                    {current.back}
                  </p>
                )}
              </div>
            </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* ─── State guidance — barely-there whisper text near the bottom ── */}
      <AnimatePresence>
        {!flashOnly && guidance && !exiting && (
          <motion.p
            key={guidance.text}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
            className="absolute italic text-center px-8 max-w-md"
            style={{
              bottom: "9%",
              left: 0,
              right: 0,
              color: "rgba(255,255,255,0.72)",
              fontFamily: JOURNEY_FONT_BODY,
              fontStyle: "italic",
              fontWeight: 300,
              fontSize: "clamp(12px, 2.0vw, 15px)",
              letterSpacing: "0.01em",
            }}
            data-testid="rapid-guidance"
          >
            {guidance.text}
          </motion.p>
        )}
      </AnimatePresence>

      {/* Tiny step label */}
      {!flashOnly && (
      <p
        className="absolute top-9 left-0 right-0 text-center text-[10px] uppercase tracking-[0.42em]"
        style={{ color: P.accentBlue, fontFamily: JOURNEY_FONT_LABEL, fontWeight: 500 }}
      >
        Install
      </p>
      )}
    </motion.div>
  );
}
